"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatePicker = exports.DatePickerElement = void 0;
// DatePicker.ts
const web_components_1 = require("../web-components");
const DatePickerStyles_1 = require("./DatePickerStyles");
class DatePickerElement extends HTMLElement {
    shadowRoot;
    inputElement = null;
    // State tracking
    hasRendered = false; // Prevent duplicate initial renders
    lastEmittedValue = ""; // Track to prevent duplicate events
    static get observedAttributes() {
        return [
            "value",
            "min",
            "max",
            "disabled",
            "name",
            "locale",
            "format",
            "placeholder",
        ];
    }
    constructor() {
        super();
        this.shadowRoot = (0, web_components_1.safeAttachShadow)(this, "open");
    }
    connectedCallback() {
        if (!this.hasRendered) {
            this.render(); // render once on mount
            this.hasRendered = true;
        }
    }
    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.hasRendered)
            return; // Skip during construction
        if (oldValue === newValue)
            return; // Skip if value unchanged
        if (name === "value" && this.inputElement) {
            // Update input value without re-render
            if (this.inputElement.value !== newValue) {
                this.inputElement.value = newValue || "";
                this.lastEmittedValue = newValue || "";
            }
        }
        else if (name === "disabled" && this.inputElement) {
            if (newValue !== null)
                this.inputElement.setAttribute("disabled", "");
            else
                this.inputElement.removeAttribute("disabled");
        }
        else {
            this.render(); // For structural changes (min/max/name), do full re-render
        }
    }
    // Public API
    get value() {
        return this.getAttribute("value") ?? "";
    }
    set value(val) {
        if (val)
            this.setAttribute("value", val);
        else
            this.removeAttribute("value");
    }
    // Handle input event (fires during month navigation), don't emit change events on input
    onInput = () => {
        // Update internal value but don't emit change event
        const v = this.inputElement?.value || "";
        // Only update attribute, don't dispatch event
        if (v !== this.getAttribute("value")) {
            super.setAttribute("value", v); // Use super to avoid triggering attributeChangedCallback
        }
    };
    // Handle change event (fires on final date selection only) only emit on final selection
    onChange = () => {
        const v = this.inputElement?.value || "";
        if (v === this.lastEmittedValue)
            return; // Prevent duplicate emissions
        this.lastEmittedValue = v;
        // Update attribute
        this.setAttribute("value", v);
        // Dispatch bubbling change event
        this.dispatchEvent(new CustomEvent("change", {
            bubbles: true,
            composed: true,
            detail: { value: v },
        }));
    };
    render() {
        console.log("render(): [DatePicker] Render called ---------------");
        const id = this.getAttribute("id") || `date-picker-${Date.now()}`;
        const value = this.getAttribute("value") ?? "";
        const min = this.getAttribute("min") ?? "";
        const max = this.getAttribute("max") ?? "";
        const disabled = this.hasAttribute("disabled");
        const name = this.getAttribute("name") ?? "";
        // Track last emitted value on initial render
        this.lastEmittedValue = value;
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: inline-block;
                    font: inherit;
                    cursor: default;
                }

                .DatePicker { ${DatePickerStyles_1.styles.datePicker} }
                .DatePicker__trigger { ${DatePickerStyles_1.styles.trigger} }
                .DatePicker__icons { ${DatePickerStyles_1.styles.icons} }
                #date-picker-native-input-${id} { ${DatePickerStyles_1.styles.nativeInput} }
            </style>
            <div class="DatePicker" part="DatePicker">
                <!-- Native input positioned over entire component -->
                <input class="DatePicker__native-input" part="native-input" id="date-picker-native-input-${id}" type="date"
                    ${value ? `value="${value}"` : ""}
                    ${min ? `min="${min}"` : ""}
                    ${max ? `max="${max}"` : ""}
                    ${disabled ? "disabled" : ""}
                    ${name ? `name="${name}"` : ""}
                />

                <!-- Visual trigger underneath (no click handler needed) -->
                <button class="DatePicker__trigger" type="button" part="trigger" aria-haspopup="dialog" aria-expanded="false">
                    <span class="DatePicker__icons" part="icons">
                        <dc-icon part="calendar-icon" family="regular" name="calendar" size="md"></dc-icon>
                    </span>
                    <span class="DatePicker__icons" part="dropdown-icons">
                        <dc-icon part="dropdown-icon" family="regular" name="chevron-down" size="md"></dc-icon>
                    </span>
                </button>

            </div>
        `;
        // Cache input element
        this.inputElement = this.shadowRoot.querySelector(`#date-picker-native-input-${id}`);
        // Remove old listeners before attaching
        if (this.inputElement) {
            this.inputElement.removeEventListener("input", this.onInput);
            this.inputElement.removeEventListener("change", this.onChange);
            this.inputElement.addEventListener("input", this.onInput);
            this.inputElement.addEventListener("change", this.onChange);
        }
    }
    disconnectedCallback() {
        // cleanup event listeners
        if (this.inputElement) {
            this.inputElement.removeEventListener("input", this.onInput);
            this.inputElement.removeEventListener("change", this.onChange);
        }
    }
}
exports.DatePickerElement = DatePickerElement;
// Minimal helper (match your PageBanner pattern)
const DatePicker = (props) => {
    const { value, min, max, name, disabled, locale, format, placeholder } = props.attributes;
    const id = props.attributes.id || `date-picker-${Date.now()}`;
    return `
        <date-picker ${(0, web_components_1.toAttribute)("id", id)}
            ${(0, web_components_1.toAttribute)("value", value)}
            ${(0, web_components_1.toAttribute)("min", min)}
            ${(0, web_components_1.toAttribute)("max", max)}
            ${(0, web_components_1.toAttribute)("name", name)}
            ${(0, web_components_1.toAttribute)("locale", locale)}
            ${(0, web_components_1.toAttribute)("format", format)}
            ${(0, web_components_1.toAttribute)("placeholder", placeholder)}
            ${disabled ? "disabled" : ""}
        ></date-picker>
    `;
};
exports.DatePicker = DatePicker;
(0, web_components_1.registerWebComponent)("date-picker", DatePickerElement);
//# sourceMappingURL=DatePicker.js.map